/**
 * FacetSection.jsx
 *
 * Collapsible filter section for product filtering, used by the Tool
 * Finder Results page facet rail. Vibrant-orange checkbox accent matches
 * Interactive Catalog facet design.
 *
 * PORTED VERBATIM from TH ToolFinder v4 (Group D Sub-Plan 3, Task SP3.B.1):
 *   - Source: `tophammer-toolfinder-v4/src/components/product/FacetSection.jsx`
 *     (77 lines, full file).
 *
 * Behaviour contract (preserved from TF v4):
 *   - Local state: `isOpen` (boolean, default true).
 *   - Returns null when `facet` or `facet.id` is missing.
 *   - Returns null when no facet values are visible (count > 0 OR currently
 *     selected).
 *   - Selected-count chip in vibrant-orange next to the label.
 *   - Caps the rendered list at 10 values and shows a "Show N more"
 *     overflow link (presentational only — no expand handler in source).
 *   - Checked values render with `text-carbon-black` + `font-medium`,
 *     unchecked with `text-n7`.
 *   - `onToggle(facet.id, value.value)` fires on row click.
 *
 * MRT adaptations (only):
 *   - Replaced `import { Icon } from '../ui'` with named lucide-react
 *     icons: `ChevronUp`, `ChevronDown`, `Check`.
 *   - TF v4 `expand_less`/`expand_more` → `ChevronUp`/`ChevronDown`.
 *   - TF v4 `check` (size 14) → `<Check size={14} />`.
 *   - Sandvik no-rounded-corners rule: checkbox div uses `rounded-none`
 *     instead of `rounded` (Sandvik brand bans rounded surfaces).
 *
 * @module components/product/FacetSection
 * @since 2026-05-18 (Group D Sub-Plan 3, Task SP3.B.1)
 */
import { useState } from 'react';
import { ChevronUp, ChevronDown, Check } from 'lucide-react';

export function FacetSection({ facet, activeFilters = {}, onToggle }) {
  const [isOpen, setIsOpen] = useState(true);

  // Defensive: ensure facet exists and has expected structure
  if (!facet || !facet.id) {
    return null;
  }

  const selectedValues = activeFilters[facet.id] || [];

  // Defensive: ensure values is an array
  const facetValues = Array.isArray(facet.values) ? facet.values : [];
  const visibleValues = facetValues.filter(v => v && (v.count > 0 || selectedValues.includes(v.value)));

  // Don't render if no visible values
  if (visibleValues.length === 0) return null;

  return (
    <div className="border-b border-n2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-n1 transition-colors"
      >
        <span className="font-medium text-sm">
          {facet.label || facet.id}
          {selectedValues.length > 0 && (
            <span className="ml-2 text-vibrant-orange">({selectedValues.length})</span>
          )}
        </span>
        {isOpen ? (
          <ChevronUp size={20} className="text-n5" />
        ) : (
          <ChevronDown size={20} className="text-n5" />
        )}
      </button>

      {isOpen && (
        <div className="px-4 pb-3 space-y-2">
          {visibleValues.slice(0, 10).map((option) => {
            if (!option || option.value == null) return null;

            const isChecked = selectedValues.includes(option.value);
            return (
              <div
                key={String(option.value)}
                className="flex items-center gap-3 py-1.5 cursor-pointer group"
                onClick={() => onToggle && onToggle(facet.id, option.value)}
              >
                <div className={`w-5 h-5 rounded-none border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                  isChecked ? 'bg-vibrant-orange border-vibrant-orange' : 'bg-white border-n3 group-hover:border-n5'
                }`}>
                  {isChecked && <Check size={14} className="text-white" />}
                </div>
                <span className={`text-sm flex-1 truncate ${isChecked ? 'font-medium text-carbon-black' : 'text-n7'}`}>
                  {option.label || String(option.value)}
                </span>
                <span className="text-xs text-n5">({option.count || 0})</span>
              </div>
            );
          })}
          {visibleValues.length > 10 && (
            <button className="text-sm text-electric-blue hover:underline mt-1">
              Show {visibleValues.length - 10} more
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default FacetSection;
